import React, { useState } from "react";
import { apiPost } from "../api";
import { TASK_CATEGORIES, taskYearEndDate } from "../utils/taskFields";

const label = { display: 'block', fontSize: 12, color: '#374151', marginBottom: 4 };
const input = { width: '100%', padding: '6px 8px', border: '1px solid #d1d5db', borderRadius: 4, fontSize: 14, background: 'white' };
const readOnly = { ...input, background: '#f9fafb', color: '#6b7280' };

// "must make a unique set" is the database talking. The rule it is reporting
// is a real one -- a client may have only ONE open task per category per
// fiscal year (Task.Meta unique_open_task_per_client_year_type) -- and the
// answer is always the same: the task you are trying to create is already
// there, so pick it instead of making a second one.
function readableError(e, { category, fiscalYear }) {
  const data = e?.response?.data;
  const nonField = data?.non_field_errors;
  if (Array.isArray(nonField) && nonField.some((m) => String(m).includes('unique set'))) {
    const name = (TASK_CATEGORIES.find((c) => c.value === category) || {}).label || category;
    return `This client already has an open ${name} task for ${fiscalYear}. `
      + `Close or rename that one, or pick it from the list instead.`;
  }
  if (Array.isArray(nonField) && nonField.length) return String(nonField[0]);
  if (data && typeof data === 'object') {
    const first = Object.entries(data)[0];
    if (first) return `${first[0]}: ${Array.isArray(first[1]) ? first[1][0] : first[1]}`;
  }
  return e?.message || 'Failed to create';
}

// The New Task form, shared by the client page's "+ New Task" button and the
// time-entry grid's create row.
//
// Every field a task needs to take time and land in the right year is here and
// filled in: the fiscal year (which is what stamps the entry's Year End), the
// category, the name the picker will label it with, and the year-end DATE the
// planning grid and roll-forward read. A task created without those is
// bookable but unplannable, and reads as "Corporate FY2026" in every picker.
export default function NewTaskModal({
  clientId, clientName, isCorporate, fiscalMonthEnd, taskTypeOptions = [],
  defaultName = '', defaultFiscalYear, onClose, onCreated,
}) {
  const [category, setCategory] = useState(isCorporate ? 'corporate' : 'personal');
  const [name, setName] = useState(defaultName);
  const [fiscalYear, setFiscalYear] = useState(
    defaultFiscalYear ? String(defaultFiscalYear) : String(new Date().getFullYear()),
  );
  const [taskTypeId, setTaskTypeId] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');

  const year = parseInt(fiscalYear, 10);
  const yearEnd = Number.isFinite(year) ? taskYearEndDate(year, isCorporate, fiscalMonthEnd) : null;

  const submit = async (e) => {
    e.preventDefault();
    // The fiscal year is what ties time to a year, so it is required here
    // even though the column is nullable -- a task created without one
    // silently declines to stamp anything on the time booked against it.
    if (!Number.isFinite(year)) { setErr('A fiscal year is required.'); return; }
    setBusy(true);
    setErr('');
    try {
      const created = await apiPost('/api/tasks/', {
        client: parseInt(clientId, 10),
        task_category: category,
        fiscal_year: year,
        name: name.trim(),
        year_end_date: yearEnd,
        task_type: taskTypeId ? parseInt(taskTypeId, 10) : null,
      });
      onCreated(created);
    } catch (e2) {
      setErr(readableError(e2, { category, fiscalYear: year }));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true"
           aria-label="New Task"
           style={{ background: 'white', borderRadius: 8, width: 400, padding: 24 }}>
        <h3 style={{ marginTop: 0 }}>New Task</h3>
        <form onSubmit={submit}>
          {clientName && (
            <div style={{ marginBottom: 12 }}>
              <label style={label} htmlFor="nt-client">Client</label>
              <input id="nt-client" value={clientName} readOnly style={readOnly} />
            </div>
          )}
          <div style={{ marginBottom: 12 }}>
            <label style={label} htmlFor="nt-name">Name</label>
            <input id="nt-name" value={name} onChange={(e) => setName(e.target.value)}
                   placeholder="e.g. T2 Return" style={input} autoFocus />
          </div>
          <div style={{ marginBottom: 12 }}>
            <label style={label} htmlFor="nt-category">Category</label>
            <select id="nt-category" value={category} onChange={(e) => setCategory(e.target.value)} style={input}>
              {TASK_CATEGORIES.map((c) => (
                <option key={c.value} value={c.value}>{c.label}</option>
              ))}
            </select>
          </div>
          <div style={{ marginBottom: 12 }}>
            <label style={label} htmlFor="nt-fy">Fiscal Year</label>
            <input id="nt-fy" type="number" value={fiscalYear}
                   onChange={(e) => setFiscalYear(e.target.value)} style={input} />
            {/* The year end this implies, spelled out: FY2026 means one date
                for a June-30 corporate and another for everyone else, and
                that date is what the planning grid files the task under. */}
            {yearEnd && (
              <div style={{ fontSize: 12, color: '#6b7280', marginTop: 4 }}>
                Year end {yearEnd} · time booked here will be filed under {year}
              </div>
            )}
          </div>
          <div style={{ marginBottom: 12 }}>
            <label style={label} htmlFor="nt-type">Task Type (optional)</label>
            <select id="nt-type" value={taskTypeId} onChange={(e) => setTaskTypeId(e.target.value)} style={input}>
              <option value="">— None —</option>
              {taskTypeOptions.map((tt) => (
                <option key={tt.id} value={tt.id}>{tt.name}</option>
              ))}
            </select>
          </div>
          {err && <div role="alert" style={{ color: '#dc2626', marginBottom: 12, fontSize: 13 }}>{err}</div>}
          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
            <button type="button" onClick={onClose} disabled={busy}
                    style={{ padding: '6px 12px', border: '1px solid #d1d5db', borderRadius: 4, background: 'white', cursor: 'pointer' }}>
              Cancel
            </button>
            <button type="submit" disabled={busy}
                    style={{ padding: '6px 12px', border: 'none', borderRadius: 4, background: '#059669', color: 'white', cursor: 'pointer', fontWeight: 600 }}>
              {busy ? 'Creating…' : 'Create Task'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
